import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { ChartContainer } from '../../../../common/chart/chartContainer'
import Bar from '../../../../common/chart/chartContainer/Bar'
import Line from '../../../../common/chart/chartContainer/Line'
import { MARGIN_CHART } from '../../../../common/chart/chartContainer/constants'
import { Legend } from '../../../../common/chart/legend'
import { Element } from '../../../../common/html/element'
import { Notification } from '../../../../common/notification'
import { SCREEN } from '../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../common/selectCurrencyUnit/useFormatCurrency'
import { formatVal, valToPercent } from '../../../../helpers/value'
import { CURRENCY } from '../../../../utils/currency'
import { selectCompanyInfo } from '../../../common/header/companyInfo/store/slice'
import { CustomTooltip } from '../../../industry/industryOverview/macroeconomics/customTooltip'
import { selectDataTable, selectShowListChart } from './store/slice'

export const ChartContent = () => {
  const { convertValue } = useFormatCurrency(SCREEN.PEER_ANALYSIS)

  const data = useSelector(selectDataTable)
  const showListChart = useSelector(selectShowListChart)
  const { organizationId, organizationName } = useSelector(selectCompanyInfo)
  const locale = useSelector((state) => state.i18n.locale)

  const orgIds = showListChart.map((item) => item.val)
  const orgList = orgIds.map((id) => data[id])

  const barChart = Object.values(data[organizationId] || {})
    .filter((item) => item.realYear)
    .map((item) => ({
      label: item.realYear,
      data:
        item.unit === CURRENCY.VND
          ? convertValue({ value: item.value, year: item.realYear })
          : item.value,
      unit: item.unit,
    }))

  const lineChart = orgIds.map((item) => {
    return Object.values(data[item] || [])
      .filter((item) => item.realYear)
      .map((item) => {
        return {
          label: item.realYear,
          data:
            item.unit === CURRENCY.VND
              ? convertValue({ value: item.value, year: item.realYear })
              : item.value,
          unit: item.unit,
        }
      })
  })

  const dataFormat = lineChart.map((item) => {
    let obj = {}

    item.forEach((i) => {
      obj[i.label] = i.data
    })

    return obj
  })

  const objData = {}

  dataFormat[0] &&
    Object.keys(dataFormat[0]).forEach((key) => {
      dataFormat.forEach((item) => {
        objData[key] = [...(objData[key] || []), item[key]]
      })
    })

  const fullData = barChart.map((item) => {
    const eachYear = objData[item.label] || []
    const obj = {}

    eachYear.forEach((i, index) => {
      obj['line' + index] = i
    })

    return {
      ...item,
      ...obj,
    }
  })

  const getFormattedData = (val) => {
    const unit = fullData.length ? fullData[0].unit : ''
    switch (unit) {
      case '%':
        return valToPercent(val, locale)

      default:
        return formatVal(val, locale)
    }
  }

  if (!fullData.length) {
    return <Notification />
  }

  return (
    <div className="flexColumn h100">
      <ChartContainer
        data={fullData}
        width={'100%'}
        height={330}
        margin={{ ...MARGIN_CHART, left: 0 }}
        yAxis={[
          {
            yAxisId: 'left',
            dataKeys: ['data', ...orgList.map((_, index) => 'line' + index)],
            isBarChart: true,
            tickFormatter: getFormattedData,
          },
        ]}
        xAxis={[{ dataKey: 'label' }]}
        tooltip={{
          maxWidth: 300,
          schema: {
            renderCustomContent: ({ data = {} }) => {
              return (
                <CustomTooltip
                  label={data.label}
                  list={[
                    {
                      label: organizationName,
                      value: getFormattedData(data.data),
                    },
                    ...orgList.map((item, index) => ({
                      label: item.organizationName,
                      value: getFormattedData(data['line' + index]),
                    })),
                  ]}
                />
              )
            },
          },
        }}
      >
        <Bar yAxisId="left" dataKey="data" fill="#006AA6" barSize={32} />
        {orgList.map((item, index) => {
          const { color } = showListChart.find((i) => i.val === item.col1) || {}
          return (
            <Line
              key={index}
              yAxisId="left"
              dataKey={'line' + index}
              stroke={color}
              fill={color}
            />
          )
        })}
      </ChartContainer>

      <Legend
        items={[
          {
            color: '#006AA6',
            title: organizationName,
            isI18n: false,
          },
        ]}
      />
      {showListChart.length > 0 && (
        <div style={{ margin: '4px 0' }}>
          <Translate
            value="companyAnalysis.peerAnalysis.SELECTED_PEERS"
            className="fw600"
          />
        </div>
      )}
      <Legend
        className="flexColumn"
        items={showListChart.map((item) => ({
          key: item.val,
          color: item.color,
          title:
            orgList.find((i) => i.col1 === item.val)?.organizationName || '',
          iconCustom: true,
          isEllipsis: true,
          isI18n: false,
          renderCustomIcon: () => {
            return (
              <Element
                style={{
                  height: 4,
                  width: 12,
                  backgroundColor: item.color,
                  cursor: 'default',
                }}
              />
            )
          },
        }))}
      />
    </div>
  )
}
